import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  AppBar,
  Toolbar,
  Grid,
} from "@mui/material";
import {
  RocketLaunch,
  Security,
  Speed,
  DashboardCustomize,
} from "@mui/icons-material";
import "./Home.css";
import LoginPanel from "../../LoginPanel/LoginPanel";
import PDFViewer from "../../scenes/jasperReports/PDFViewer";
import PDFViewerInvoice from "../../scenes/jasperReports/PDFViewerInvoice";
const Home = ({ onLogin }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [labRequestId, setLabRequestId] = useState(null);
  const [reportNo, setReportNo] = useState(null);

  // Parse URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const requestId = urlParams.get("labRequestId");
    const reportNo = urlParams.get("reportNo");

    if (requestId) {
      setLabRequestId(requestId);
      setReportNo(reportNo);
    }
  }, []);

  const features = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Lightning Fast",
      description:
        "Optimized performance with real-time updates and seamless user interactions.",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Enterprise Security",
      description:
        "Military-grade encryption and secure authentication protocols for your data.",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    },
    {
      icon: <DashboardCustomize sx={{ fontSize: 40 }} />,
      title: "Smart Analytics",
      description:
        "Advanced analytics and insights to drive your business decisions forward.",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)",
    },
  ];

  // If labRequestId is present, show PDF viewer directly
  if (labRequestId) {
    return (
      <Box className="hero-section">
        <AppBar position="fixed" elevation={0} className="glass-navbar">
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <RocketLaunch sx={{ mr: 2, color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Material Laboratory System - Kekirawa
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ pt: 10, px: { xs: 1, sm: 2 } }}>
          <Box className="fade-in">
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                mb: 3,
                fontSize: { xs: "1.5rem", sm: "2rem" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              {reportNo.startsWith("QC")
                ? "Lab Report No - #" + reportNo
                : "Invoice No - #" + reportNo}
            </Typography>

            {reportNo.startsWith("QC") ? (
              <PDFViewer
                reportNo={reportNo}
                labRequestId={labRequestId}
                letterHead={true}
              />
            ) : (
              <PDFViewerInvoice
                reportNo={reportNo}
                labRequestId={labRequestId}
                letterHead={true}
                jobId={592}
              />
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="hero-section">
      {/* Navigation */}
      <AppBar position="fixed" elevation={0} className="glass-navbar">
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <RocketLaunch
              sx={{
                mr: 2,
                color: "primary.main",
                fontSize: { xs: 20, sm: 24 },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Material Laboratory System - Kekirawa
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => setShowLogin(true)}
            sx={{
              background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
              borderRadius: 3,
              px: { xs: 2, sm: 4 },
              py: 1,
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
            }}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ pt: 10, px: { xs: 2, sm: 3 } }}>
        {!showLogin ? (
          <Box className="fade-in">
            {/* Hero Section */}
            <Box sx={{ textAlign: "center", py: { xs: 4, md: 8 } }}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  mb: 3,
                  fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
                }}
              >
                MatLab
              </Typography>
              <Typography
                variant="h4"
                color="text.secondary"
                sx={{
                  mb: 4,
                  maxWidth: "600px",
                  mx: "auto",
                  fontWeight: 400,
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                }}
              >
                We serve a diverse clientele including government agencies,
                construction firms, and private enterprises across multiple
                states.
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={() => setShowLogin(true)}
                startIcon={<RocketLaunch />}
                sx={{
                  px: { xs: 4, sm: 6 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  boxShadow: "0 10px 30px rgba(37, 99, 235, 0.3)",
                }}
              >
                Launch Dashboard
              </Button>
            </Box>

            {/* Features Grid */}
            <Grid container spacing={3} sx={{ py: { xs: 4, md: 8 } }}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box
                    className="feature-card slide-up"
                    sx={{
                      textAlign: "center",
                      p: { xs: 3, sm: 4 },
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        mb: 3,
                        borderRadius: 3,
                        background: feature.gradient,
                        color: "white",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <LoginPanel
            onLogin={(userData) => {
              setShowLogin(false);
              onLogin(userData);
            }}
            onClose={() => setShowLogin(false)}
          />
        )}
      </Container>
    </Box>
  );
};

export default Home;
